'use strict';

/**
 * Image360Modal component
 * Handles full-screen 360° product view modal with smooth drag, touch scrubbing, inertia, preloading, and keyboard controls.
 */
function Image360Modal() {
    this.$modal = null;
    this.$overlay = null;
    this.$viewport = null;
    this.$container = null;
    this.$frameImg = null;
    this.$loader = null;
    this.$progressBar = null;

    this.images = [];
    this.loadedImages = [];
    this.frameCount = 0;
    this.currentFrame = 0;

    this.isDragging = false;
    this.startX = 0;
    this.lastX = 0;
    this.dragStartFrame = 0;
    this.velocity = 0;
    this.sensitivity = 10; // Pixels per frame step
    this.animFrameReq = null;
    this.isOpen = false;
}

Image360Modal.prototype.init = function () {
    this.cacheDOM();
    if (!this.$modal) {
        return;
    }
    this.bindGlobalEvents();
};

Image360Modal.prototype.cacheDOM = function () {
    var modalEl = document.getElementById('rolex-360-modal') || document.querySelector('.rolex-360-modal');
    if (!modalEl) return;

    this.$modal = modalEl;
    this.$viewport = modalEl.querySelector('[data-360-viewport]');
    this.$container = modalEl.querySelector('[data-360-container]');
    this.$frameImg = modalEl.querySelector('[data-360-frame]');
    this.$loader = modalEl.querySelector('[data-360-loader]');
    this.$progressBar = modalEl.querySelector('[data-360-progress]');
};

Image360Modal.prototype.bindGlobalEvents = function () {
    var self = this;

    // Delegate click on any 360 trigger button
    document.addEventListener('click', function (e) {
        var trigger = e.target.closest('[data-360-trigger], .btn-360-trigger');
        if (trigger) {
            e.preventDefault();
            self.openWithTrigger(trigger);
        }

        // Close triggers inside modal
        if (e.target.closest('[data-360-close]')) {
            e.preventDefault();
            self.close();
        }
    });

    // Keyboard events
    document.addEventListener('keydown', function (e) {
        if (!self.isOpen) return;

        if (e.key === 'Escape') {
            self.close();
        } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            self.stopInertia();
            self.prevFrame();
        } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            self.stopInertia();
            self.nextFrame();
        }
    });

    // Viewport drag/touch events
    if (this.$viewport) {
        this.bindInteractionEvents();
    }
};

Image360Modal.prototype.openWithTrigger = function (triggerEl) {
    var imageList = [];
    var rawImages = triggerEl.getAttribute('data-360-images');
    var baseUrl = triggerEl.getAttribute('data-360-base-url');
    var extension = triggerEl.getAttribute('data-360-extension') || '.jpg';
    var count = parseInt(triggerEl.getAttribute('data-360-count'), 10) || 36;

    if (rawImages) {
        try {
            imageList = JSON.parse(rawImages);
        } catch (e) {
            imageList = [];
        }
    } else if (baseUrl) {
        var imageDivs = document.querySelectorAll('.item-360-images-wrapper');

        if (imageDivs && imageDivs.length > 0) {
            imageDivs.forEach(function (div) {
                var url = div.getAttribute('data-360-base-url');
                if (url) {
                    imageList.push(url);
                }
            });

            imageList.sort(function (a, b) {
                var numA = parseInt((a.match(/_(\d+)\.\w+$/) || [])[1], 10) || 0;
                var numB = parseInt((b.match(/_(\d+)\.\w+$/) || [])[1], 10) || 0;
                return numA - numB;
            });
        }
    }

    if (!imageList || imageList.length === 0) {
        return;
    }

    this.images = imageList;
    this.frameCount = imageList.length;
    this.currentFrame = 0;
    this.open();
};

Image360Modal.prototype.open = function () {
    if (!this.$modal) this.cacheDOM();
    if (!this.$modal) return;

    this.isOpen = true;
    this.$modal.classList.add('is-active');
    this.$modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    this.preloadImages();
};

Image360Modal.prototype.close = function () {
    if (!this.isOpen) return;

    this.stopInertia();
    this.isOpen = false;
    this.$modal.classList.remove('is-active');
    this.$modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
};

Image360Modal.prototype.preloadImages = function () {
    var self = this;
    var loadedCount = 0;
    this.loadedImages = [];

    if (this.$loader) {
        this.$loader.style.display = 'flex';
    }
    if (this.$progressBar) {
        this.$progressBar.textContent = '0';
    }

    this.images.forEach(function (src, index) {
        var img = new Image();
        img.onload = img.onerror = function () {
            loadedCount++;
            var pct = Math.round((loadedCount / self.frameCount) * 100);
            if (self.$progressBar) {
                self.$progressBar.textContent = pct;
            }

            if (loadedCount >= self.frameCount) {
                if (self.$loader) {
                    self.$loader.style.display = 'none';
                }
                self.updateFrame();
            }
        };
        img.src = src;
        self.loadedImages[index] = img;
    });

    // Set initial image
    if (this.images.length > 0 && this.$frameImg) {
        this.$frameImg.src = this.images[0];
    }
};

Image360Modal.prototype.bindInteractionEvents = function () {
    var self = this;
    var target = this.$viewport;

    function getPageX(e) {
        if (e.touches && e.touches.length > 0) {
            return e.touches[0].pageX;
        }
        return e.pageX || 0;
    }

    function onStart(e) {
        if (!self.isOpen || self.frameCount === 0) return;
        self.stopInertia();
        self.isDragging = true;
        self.velocity = 0;

        var pageX = getPageX(e);
        self.startX = pageX;
        self.lastX = pageX;
        self.dragStartFrame = self.currentFrame;

        target.classList.add('is-grabbing');
    }

    function onMove(e) {
        if (!self.isDragging) return;

        var pageX = getPageX(e);
        var deltaX = pageX - self.startX;
        var stepDelta = pageX - self.lastX;
        self.velocity = stepDelta;
        self.lastX = pageX;

        var frameOffset = Math.round(-deltaX / self.sensitivity);
        var nextFrame = (self.dragStartFrame + frameOffset) % self.frameCount;
        if (nextFrame < 0) {
            nextFrame = self.frameCount + (nextFrame % self.frameCount);
        }

        if (nextFrame !== self.currentFrame) {
            self.currentFrame = nextFrame % self.frameCount;
            self.updateFrame();
        }
    }

    function onEnd() {
        if (!self.isDragging) return;
        self.isDragging = false;
        target.classList.remove('is-grabbing');
        self.applyInertia();
    }

    target.addEventListener('mousedown', onStart);
    target.addEventListener('touchstart', onStart, { passive: true });

    window.addEventListener('mousemove', onMove);
    window.addEventListener('touchmove', onMove, { passive: true });

    window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchend', onEnd);
    window.addEventListener('touchcancel', onEnd);
};

Image360Modal.prototype.applyInertia = function () {
    var self = this;
    if (Math.abs(this.velocity) < 1) return;

    function step() {
        if (self.isDragging || Math.abs(self.velocity) < 0.2) {
            self.stopInertia();
            return;
        }

        if (self.velocity > 0) {
            self.prevFrame();
        } else {
            self.nextFrame();
        }

        self.velocity *= 0.90; // Inertia friction factor
        self.animFrameReq = requestAnimationFrame(step);
    }

    this.animFrameReq = requestAnimationFrame(step);
};

Image360Modal.prototype.stopInertia = function () {
    if (this.animFrameReq) {
        cancelAnimationFrame(this.animFrameReq);
        this.animFrameReq = null;
    }
    this.velocity = 0;
};

Image360Modal.prototype.nextFrame = function () {
    if (this.frameCount === 0) return;
    this.currentFrame = (this.currentFrame + 1) % this.frameCount;
    this.updateFrame();
};

Image360Modal.prototype.prevFrame = function () {
    if (this.frameCount === 0) return;
    this.currentFrame = (this.currentFrame - 1 + this.frameCount) % this.frameCount;
    this.updateFrame();
};

Image360Modal.prototype.updateFrame = function () {
    if (this.$frameImg && this.images[this.currentFrame]) {
        this.$frameImg.src = this.images[this.currentFrame];
    }
};

var modalInstance = new Image360Modal();

module.exports = {
    init: function () {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', function () {
                modalInstance.init();
            });
        } else {
            modalInstance.init();
        }
    },
    getInstance: function () {
        return modalInstance;
    }
};
